/**
 * SASADM-0152 — the Reseller Admin sub-tab: the product's reseller console, an app with
 * role `RESELLER_ADMIN` (saas-auth SASATH-0336). Create (gated on the product's Product
 * Admin app), edit and delete it through ProductApps' `AppDialog` / app API; show its
 * console URL and its derived hostnames with Check DNS through the shared
 * ConsoleHostnamesTable, filtered to this app's rows. Resellers, their users and tenant
 * assignment are managed in the product-admin app, not here.
 *
 * Mirrors ProductAdmins: a SECTION that self-fetches after mount, SUPER_ADMIN-only and
 * double-guarded — ProductDetail only offers the sub-tab to a super principal, and this
 * component returns null (and never fetches) for anyone else. A failed apps read is an
 * error state, never the "no console" state, so Add is never offered over a console that
 * may exist.
 *
 * Hosts are derived, never typed: saas-auth mints them on create and removes them on
 * delete. None of the reseller rows is primary, so a row is a platform row when the product
 * has no base domain or the hostname is not under it.
 */
import { Alert, Box, Button, Link, Paper, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError, type ErrorCode } from '@/api/errors';
import { isDeployProblem } from '@/api/routingDeploy';
import { useCurrentOperator } from '@/auth/AuthContext';
import type { ProductAppDto, ProductHostnameDto, RoutingDeployOutcome } from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ConsoleHostnamesTable } from '@/components/ConsoleHostnamesTable';
import { CopyToClipboardButton } from '@/components/CopyToClipboardButton';
import { RoutingDeployBanner } from '@/components/RoutingDeployBanner';
import { useSnackbar } from '@/components/Snackbar';
import { useCopy } from '@/hooks/useCopy';
import { AppDialog } from './ProductApps';

export function ProductResellerAdmin({
  productId,
  productSlug,
  baseDomain,
}: {
  productId: number;
  productSlug: string;
  baseDomain: string | null;
}) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const operator = useCurrentOperator();
  const isSuperAdmin = operator?.isSuperAdmin === true;
  const [apps, setApps] = useState<ProductAppDto[] | null>(null);
  const [appsLoadFailed, setAppsLoadFailed] = useState(false);
  const [hostnames, setHostnames] = useState<ProductHostnameDto[] | null>(null);
  const [hostnamesLoadFailed, setHostnamesLoadFailed] = useState(false);
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // SASADM-0172 (0170 leftover) — a console save/delete whose routing deploy was REJECTED / FAILED.
  const [deployFailure, setDeployFailure] = useState<RoutingDeployOutcome | null>(null);

  // Plain-words copy for the reseller console's backend refusals (spec §4). A field
  // code (RESERVED_APP_SLUG) shows inline in AppDialog; the rest are snackbars.
  // INVALID_APP_DOCROOT keeps its existing `errors.codes` message.
  const errorCopy: Partial<Record<ErrorCode, string>> = {
    RESELLER_CONSOLE_REQUIRES_PRODUCT_CONSOLE: t(
      'errors.codes.RESELLER_CONSOLE_REQUIRES_PRODUCT_CONSOLE',
      "Create the product's Product Admin console first.",
    ),
    APP_ROLE_SINGLETON_CONFLICT: t(
      'products.resellerAdmin.errors.APP_ROLE_SINGLETON_CONFLICT',
      'This product already has a reseller console.',
    ),
    APP_SELF_CONTAINED_EXCLUSIVE: t(
      'products.resellerAdmin.errors.APP_SELF_CONTAINED_EXCLUSIVE',
      "Self-contained products can't have a reseller console.",
    ),
    RESERVED_APP_SLUG: t(
      'products.resellerAdmin.errors.RESERVED_APP_SLUG',
      'That name is reserved — try {{slug}}-reseller-admin.',
      { slug: productSlug },
    ),
  };

  function showError(err: unknown) {
    if (err instanceof ApiError) {
      snack.error(errorCopy[err.code] ?? t(`errors.codes.${err.code}`, err.code));
    } else {
      snack.error(t('errors.generic', 'Something went wrong. Please try again.'));
    }
  }

  async function refreshApps() {
    setAppsLoadFailed(false);
    setApps(null);
    try {
      setApps(await apiAdmin.productApps.list(productId));
    } catch (err) {
      showError(err);
      // Unknown must stay distinct from a confirmed empty list. In particular,
      // never expose Add-console after a failed read: the singleton may exist.
      setApps(null);
      setAppsLoadFailed(true);
    }
  }

  async function refreshHostnames() {
    setHostnamesLoadFailed(false);
    setHostnames(null);
    try {
      setHostnames(await apiAdmin.productHostnames.list(productId));
    } catch (err) {
      showError(err);
      setHostnames(null);
      setHostnamesLoadFailed(true);
    }
  }

  useEffect(() => {
    // Never hit the SUPER_ADMIN-only endpoints for a non-super principal.
    if (!isSuperAdmin) return;
    void (async () => {
      await refreshApps();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    void (async () => {
      await refreshHostnames();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, isSuperAdmin]);

  // Creating mints the console's hosts and deleting removes them, so both
  // reads follow every change.
  function refreshAll() {
    void refreshApps();
    void refreshHostnames();
  }

  async function handleDelete(app: ProductAppDto) {
    setDeleting(true);
    try {
      const outcome = (await apiAdmin.productApps.delete(productId, app.id))?.routingDeploy;
      // SASADM-0172 — a failed deploy is the banner, never the success snackbar.
      if (isDeployProblem(outcome)) setDeployFailure(outcome);
      else snack.success(t('products.resellerAdmin.delete.success', 'Reseller console deleted.'));
      setConfirmDelete(false);
      refreshAll();
    } catch (err) {
      showError(err);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  // Belt-and-suspenders: the sub-tab in ProductDetail is already SUPER_ADMIN-only.
  if (!isSuperAdmin) return null;

  const resellerApp = apps?.find((app) => app.role === 'RESELLER_ADMIN') ?? null;
  const productAdminApp = apps?.find((app) => app.role === 'PRODUCT_ADMIN') ?? null;
  const isPlatformRow = (row: ProductHostnameDto): boolean =>
    !baseDomain || !row.hostname.toLowerCase().endsWith(`.${baseDomain.toLowerCase()}`);
  const consoleRow =
    resellerApp === null
      ? null
      : (hostnames?.find(
          (row) => row.appId === resellerApp.id && row.hostnameType === 'APP' && isPlatformRow(row),
        ) ?? null);
  const consoleUrl = consoleRow ? `https://${consoleRow.hostname}` : null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="h3" sx={{ mb: 2 }}>
        {t('products.resellerAdmin.title', 'Reseller admin')}
      </Typography>
      {deployFailure && (
        <RoutingDeployBanner
          outcome={deployFailure}
          onClose={() => setDeployFailure(null)}
          action={
            <Link component={RouterLink} to="/operations" color="inherit">
              {t('routingDeploy.openOperations', 'Open Operations to deploy routing')}
            </Link>
          }
        />
      )}
      {appsLoadFailed ? (
        <Alert severity="error">
          {t(
            'products.resellerAdmin.consoleLoadError',
            "Reseller console details couldn't be loaded. Its existence is unknown, so adding a console is unavailable.",
          )}
        </Alert>
      ) : apps === null ? (
        <Typography color="text.secondary">{t('common.loading', 'Loading')}</Typography>
      ) : resellerApp === null ? (
        <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'products.resellerAdmin.explainer',
              "The reseller console is the app reseller companies use to manage the tenants they sell. Resellers and their users are managed in the product's admin console.",
            )}
          </Typography>
          {productAdminApp === null ? (
            <Box>
              <Button
                startIcon={<AddIcon />}
                disabled
                aria-describedby="reseller-console-add-reason"
              >
                {t('products.resellerAdmin.addConsole', 'Add reseller console')}
              </Button>
              <Typography
                id="reseller-console-add-reason"
                variant="caption"
                color="text.secondary"
                component="p"
              >
                {t(
                  'products.resellerAdmin.needsProductConsole',
                  'Create the Product Admin console first.',
                )}
              </Typography>
            </Box>
          ) : (
            <Button startIcon={<AddIcon />} onClick={() => setAppDialogOpen(true)}>
              {t('products.resellerAdmin.addConsole', 'Add reseller console')}
            </Button>
          )}
        </Stack>
      ) : (
        <Box component="section" aria-labelledby="reseller-admin-app-section">
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}
          >
            <Typography id="reseller-admin-app-section" variant="h4">
              {t('products.resellerAdmin.appSection', 'Reseller console')}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" startIcon={<EditIcon />} onClick={() => setAppDialogOpen(true)}>
                {t('common.edit', 'Edit')}
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDelete(true)}
              >
                {t('common.delete', 'Delete')}
              </Button>
            </Stack>
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
            <Typography component="p" variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
              {resellerApp.slug}
            </Typography>
            <Typography variant="body2">{resellerApp.displayName}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {resellerApp.docroot ?? '—'}
            </Typography>
          </Stack>
          <Box sx={{ mt: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
              {t('products.resellerAdmin.consoleUrl', 'Console URL')}
            </Typography>
            {consoleUrl ? (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <Link
                  href={consoleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontFamily: 'monospace' }}
                >
                  {consoleUrl}
                </Link>
                <CopyToClipboardButton value={consoleUrl} ariaLabel={t('common.copy', 'Copy')} />
              </Stack>
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <ConsoleHostnamesTable
              productId={productId}
              appId={resellerApp.id}
              hostnames={hostnames}
              loadFailed={hostnamesLoadFailed}
              isPlatformRow={isPlatformRow}
              explainer={t(
                'products.resellerAdmin.hostnamesExplainer',
                "Reseller hostnames are derived from the product slug, the platform base and (when set) the product base domain. On the product domain the console also uses the Product Admin console's auth host — verify that one on the Product Admin tab. After a successful check, run Deploy Platform Routing.",
              )}
              onRowVerified={(verified) =>
                setHostnames((prev) =>
                  prev ? prev.map((r) => (r.id === verified.id ? verified : r)) : prev,
                )
              }
            />
          </Box>
        </Box>
      )}
      {appDialogOpen ? (
        <AppDialog
          open
          productId={productId}
          productSlug={productSlug}
          appRole="RESELLER_ADMIN"
          app={resellerApp}
          knownApps={apps ?? []}
          tenantAdminExists={false}
          onBlockedRoleChange={() => undefined}
          defaults={{
            slug: `${productSlug}-reseller-admin`,
            docroot: `C:/caddy/htdocs/${productSlug}-reseller-admin`,
          }}
          errorCopy={errorCopy}
          onClose={() => setAppDialogOpen(false)}
          onSaved={() => {
            setAppDialogOpen(false);
            refreshAll();
          }}
          onDeployFailure={setDeployFailure}
        />
      ) : null}
      <ConfirmDialog
        open={confirmDelete}
        title={t('products.resellerAdmin.delete.title', 'Delete reseller console?')}
        body={t(
          'products.resellerAdmin.delete.body',
          "Deleting {{name}} removes the reseller console's hosts. Resellers keep their users and tenant assignments, but can't sign in until a reseller console exists again.",
          { name: resellerApp?.displayName ?? '' },
        )}
        severity="error"
        confirmLabel={t('common.delete', 'Delete')}
        busy={deleting}
        onConfirm={() => {
          if (resellerApp) void handleDelete(resellerApp);
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Paper>
  );
}
